import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, X, Download, Loader2, CheckCircle2, FileType2, Languages } from "lucide-react";
import { toast } from "sonner";
import { Btn, Overline } from "./ui";
import { downloadJSON } from "../lib/api";
import { exportJudicialDocx, exportJudicialPdf, exportJudicialPdfZh } from "../lib/reportExport";
import { useI18n, pickLang } from "../lib/i18n";

const POSTURE = {
  STABLE: "text-aegis-emerald border-aegis-emerald/40 bg-aegis-emerald/10",
  ELEVATED: "text-aegis-amber border-aegis-amber/40 bg-aegis-amber/10",
  CRITICAL: "text-aegis-red border-aegis-red/40 bg-aegis-red/10",
};

export default function ReportModal({ open, report, loading, evidence, matter, officer, clientHash, onClose }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(null);

  const exportFile = async (kind) => {
    setBusy(kind);
    try {
      const payload = { report, summary: report?.stats, evidence, matter, officer, clientHash, lang };
      const filename =
        kind === "docx"
          ? await exportJudicialDocx(payload)
          : kind === "pdfzh"
          ? await exportJudicialPdfZh(payload)
          : await exportJudicialPdf(payload);
      toast.success(t("exp.done", { f: filename }));
    } catch {
      toast.error("Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] grid place-items-center glass px-4 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="report-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 14, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="flex max-h-full w-full max-w-3xl flex-col rounded-2xl border border-aegis-cyan/25 bg-panel"
          >
            <div className="flex items-center justify-between gap-3 border-b border-edge px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 rounded-lg border border-aegis-cyan/30 bg-aegis-cyan/10 text-aegis-cyan">
                  <FileText size={18} />
                </span>
                <div>
                  <h3 className="font-heading text-[15px] font-medium text-slate-100">{t("rep.title")}</h3>
                  <p className="font-mono text-[11px] text-slate-400">
                    {report?.report_id || "—"}
                    {matter?.matter_id && <span className="ml-2 text-aegis-purple">{matter.matter_id}</span>}
                  </p>
                  {matter && (
                    <p className="text-[11px] text-slate-400" data-testid="report-matter-name">
                      {pickLang(matter, "name", lang)}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={onClose} data-testid="report-close-btn" className="text-slate-400 hover:text-slate-100 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-5">
              {loading && (
                <div className="flex flex-col items-center gap-3 py-16">
                  <Loader2 size={28} className="animate-spin text-aegis-cyan" />
                  <p className="text-sm text-slate-400">{t("rep.generating")}</p>
                </div>
              )}

              {!loading && report && (
                <>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`rounded-md border px-2.5 py-1 font-mono text-[11px] ${POSTURE[report.risk_posture] || POSTURE.ELEVATED}`}>
                      {t("rep.posture")}: {report.risk_posture}
                    </span>
                    {report.block && (
                      <span className="flex items-center gap-1.5 font-mono text-[11px] text-aegis-emerald">
                        <CheckCircle2 size={13} /> {t("rep.sealed", { n: report.block.index })}
                      </span>
                    )}
                    <span className="ml-auto font-mono text-[11px] text-slate-400">{report.generated_at}</span>
                  </div>

                  <h2 className="font-heading text-xl font-light leading-snug tracking-tight text-slate-100">
                    {pickLang(report, "headline", lang)}
                  </h2>

                  <div>
                    <Overline>{t("rep.summary")}</Overline>
                    <p className="mt-2 text-[13px] leading-relaxed text-slate-300">{pickLang(report, "executive_summary", lang)}</p>
                  </div>

                  <div className="rounded-lg border border-aegis-emerald/25 bg-aegis-emerald/5 p-4">
                    <Overline>{t("rep.integrity")}</Overline>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-slate-200">{pickLang(report, "integrity_statement", lang)}</p>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div>
                      <Overline>{t("rep.findings")}</Overline>
                      <ul className="mt-2 space-y-2">
                        {(report[`key_findings_${lang}`] || []).map((f, i) => (
                          <li key={f} className="flex gap-2 text-[13px] text-slate-300">
                            <span className="font-mono text-aegis-cyan">{String(i + 1).padStart(2, "0")}</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <Overline>{t("rep.actions")}</Overline>
                      <ul className="mt-2 space-y-2">
                        {(report[`recommended_actions_${lang}`] || []).map((f) => (
                          <li key={f} className="flex gap-2 text-[13px] text-slate-300">
                            <span className="font-mono text-aegis-amber">→</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[
                      ["readiness_score", "%"],
                      ["integrity_score", "%"],
                      ["evidence_sealed", ""],
                      ["block_height", ""],
                    ].map(([k, unit]) => (
                      <div key={k} className="rounded-lg border border-edge well px-3 py-2">
                        <p className="font-mono text-[9px] uppercase tracking-overline text-slate-400">{k}</p>
                        <p className="font-heading text-xl font-light text-slate-100 tabular">
                          {report.stats?.[k]}
                          {unit}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-edge px-6 py-4">
              <Btn variant="ghost" onClick={onClose} testid="report-dismiss-btn">
                {t("rep.close")}
              </Btn>
              <Btn
                variant="ghost"
                disabled={!report}
                onClick={() => downloadJSON(`${report.report_id}.json`, report)}
                testid="report-download-btn"
              >
                <Download size={15} /> {t("rep.download")}
              </Btn>
              <Btn variant="amber" disabled={!report || !!busy} onClick={() => exportFile("docx")} testid="report-docx-btn">
                {busy === "docx" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} .docx
              </Btn>
              <Btn variant="emerald" disabled={!report || !!busy} onClick={() => exportFile("pdfzh")} testid="report-pdf-zh-btn">
                {busy === "pdfzh" ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />} 中文 .pdf
              </Btn>
              <Btn variant="cyan" disabled={!report || !!busy} onClick={() => exportFile("pdf")} testid="report-pdf-btn">
                {busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileType2 size={15} />} EN .pdf
              </Btn>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
